import { Grid, IconButton, InputAdornment, Link, makeStyles } from '@material-ui/core';
import { Theme } from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import React, { ReactElement, useContext, useState } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import {
  ArrangementLayoutResponse,
  ArticleResponse,
  CountryPermission,
  useMurexinPimContextApi,
} from '../../../api/MurexinPimApi';
import { AuthorizationContext } from '../../../auth';
import { NotificationContext } from '../../../contexts/NotificationContext';
import { IRouterParams } from '../../../routes';
import { FileSelectButton } from '../../../shared/Components/FileSelectButton';
import MessageDialog from '../../../shared/Components/MessageDialog';
import PIMButton from '../../../shared/Components/PIMButton';
import PIMFormikSelect from '../../../shared/Components/PIMFormikSelect';
import PIMFormikTextField from '../../../shared/Components/PIMFormikTextField';
import PIMFormikToggle from '../../../shared/Components/PIMFormikToggle';
import PIMIconButton from '../../../shared/Components/PIMIconButton';
import PIMInformationBox from '../../../shared/Components/PIMInformationBox';
import { ERPIcon, LinkIcon, NonERPIcon } from '../../../shared/icons';
import { CircleLoader } from '../../../utils/CircleLoader';

interface IProps {
  articleId: number;
  materialGroupId: number;
  articleResponse: ArticleResponse;
  countryCode: string;
  updateValue: (view: number, articleId?: number) => void;
  reload: () => void;
}

interface ArticleRequest {
  articleId: number;
  materialGroupId: number;
  articleDescription: string;
  articleNumber: string;
  ean: string;
  containerType: string;
  productTypeCode: string;
  palletNumberOfContainers: number | null;
  overCartonNumberOfContainers: number | null;
  articleCountryNumber: string;
  deletedArrangementLayoutIds: number[];
  addedArrangementLayoutFiles: File[];
  isGlobal: boolean;
  articleNumberComplexB: string;
  priceGroup: string;
  v5TextColumn1: string;
  v5TextColumn2: string;
  color: string;
}

interface ArticleFormData extends ArticleRequest {
  ArrangementLayouts: ArrangementLayoutResponse[];
}

interface ArticleCreateRequest {
  ArticleId?: number;
  MaterialGroupId?: number;
  ArticleDescription?: string;
  ArticleNumber?: string;
  EAN?: string;
  ContainerType?: string;
  ProductTypeCode?: string;
  PalletNumberOfContainers?: number;
  OverCartonNumberOfContainers?: number;
  ArticleCountryNumber?: string;
  IsGlobal?: boolean;
  DeletedArrangementLayoutIds?: number[];
  AddedArrangementLayoutFiles?: File[];
  ArticleNumberComplexB: string;
  PriceGroup: string;
  v5TextColumn1: string;
  v5TextColumn2: string;
  color: string;
}

interface ArticleCountryRequest {
  ArticleId: number;
  ArticleDescription: string;
  ProductTypeCode: string;
  ArticleCountryNumber: string;
  DeletedArrangementLayoutIds?: number[];
  AddedArrangementLayoutFiles?: File[];
}

interface ERPArticleRequest {
  ArticleId: number;
  ArticleCountryNumber: string;
  DeletedArrangementLayoutIds?: number[];
  AddedArrangementLayoutFiles?: File[];
  IsGlobal: boolean;
  ArticleNumberComplexB: string;
  PriceGroup: string;
  v5TextColumn1: string;
  v5TextColumn2: string;
  color: string;
}

const MAX_ALLOWED_LAYOUTS = 5;

export function ArticleEditSidebar(props: IProps): ReactElement {
  const { articleId, materialGroupId, articleResponse, countryCode, updateValue, reload } = props;
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();
  const api = useMurexinPimContextApi();
  const notification = React.useContext(NotificationContext);
  const { country } = useParams<IRouterParams>();
  const { hasCountryPermission } = useContext(AuthorizationContext);
  const isAuthorizedToWrite = hasCountryPermission(country, CountryPermission.Write);
  const readOnly = !isAuthorizedToWrite;
  const articleOwner = countryCode === articleResponse.countryCode;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const editArticle = articleId > 0;
  const erpImport = articleResponse.erpImport;
  if (articleResponse.countryCode === null || articleResponse.countryCode === undefined) {
    articleResponse.countryCode = countryCode;
  }

  const productTypes = useAsync(async () => {
    const response = await api.articleService.articleServiceProductTypesDetail(countryCode);
    return response.data ?? [];
  }, [countryCode]);

  const mapArticleRequestToERPArticleRequest = (articleRequestModel: ArticleRequest) => {
    const erpArticleRequest: ERPArticleRequest = {
      ArticleId: articleRequestModel.articleId,
      ArticleCountryNumber: articleRequestModel.articleCountryNumber,
      DeletedArrangementLayoutIds: articleRequestModel.deletedArrangementLayoutIds,
      AddedArrangementLayoutFiles: articleRequestModel.addedArrangementLayoutFiles,
      IsGlobal: articleRequestModel.isGlobal,
      ArticleNumberComplexB: articleRequestModel.articleNumberComplexB,
      PriceGroup: articleRequestModel.priceGroup,
      v5TextColumn1: articleRequestModel.v5TextColumn1,
      v5TextColumn2: articleRequestModel.v5TextColumn2,
      color: articleRequestModel.color,
    };
    return erpArticleRequest;
  };

  const mapArticleRequestToArticleCountryRequest = (articleRequestModel: ArticleRequest) => {
    const articleCountryRequest: ArticleCountryRequest = {
      ArticleId: articleRequestModel.articleId,
      ArticleDescription: articleRequestModel.articleDescription,
      ProductTypeCode: articleRequestModel.productTypeCode,
      ArticleCountryNumber: articleRequestModel.articleCountryNumber,
      DeletedArrangementLayoutIds: articleRequestModel.deletedArrangementLayoutIds,
      AddedArrangementLayoutFiles: articleRequestModel.addedArrangementLayoutFiles,
    };
    return articleCountryRequest;
  };

  const mapArticleRequestToArticleCreateRequest = (articleRequestModel: ArticleRequest): ArticleCreateRequest => {
    const articleCreateRequest: ArticleCreateRequest = {
      ArticleId: articleRequestModel.articleId,
      MaterialGroupId: articleRequestModel.materialGroupId,
      ArticleDescription: articleRequestModel.articleDescription,
      ArticleNumber: articleRequestModel.articleNumber,
      EAN: articleRequestModel.ean,
      ContainerType: articleRequestModel.containerType,
      ProductTypeCode: articleRequestModel.productTypeCode,
      PalletNumberOfContainers: articleRequestModel.palletNumberOfContainers ?? undefined,
      OverCartonNumberOfContainers: articleRequestModel.overCartonNumberOfContainers ?? undefined,
      ArticleCountryNumber: articleRequestModel.articleCountryNumber,
      IsGlobal: articleRequestModel.isGlobal,
      DeletedArrangementLayoutIds: articleRequestModel.deletedArrangementLayoutIds,
      AddedArrangementLayoutFiles: articleRequestModel.addedArrangementLayoutFiles,
      ArticleNumberComplexB: articleRequestModel.articleNumberComplexB,
      PriceGroup: articleRequestModel.priceGroup,
      v5TextColumn1: articleRequestModel.v5TextColumn1,
      v5TextColumn2: articleRequestModel.v5TextColumn2,
      color: articleRequestModel.color,
    };
    return articleCreateRequest;
  };

  async function updateArticle(articleFormData: ArticleFormData) {
    // ArrangementLayouts needs to be removed from sent data to end point and
    // is therefore unused
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ArrangementLayouts, ...articleRequestModel } = articleFormData;

    try {
      if ((articleOwner && !articleResponse.erpImport) || !editArticle) {
        if (articleRequestModel.articleNumber !== articleResponse.articleNumber) {
          articleRequestModel.articleNumber = countryCode + articleRequestModel.articleNumber;
        }
        const articleCreateRequest = mapArticleRequestToArticleCreateRequest(articleRequestModel);
        await api.articleService.articleServiceArticleCreate(countryCode, articleId, articleCreateRequest);
      } else if (articleOwner && articleResponse.erpImport) {
        const erpArticleRequest = mapArticleRequestToERPArticleRequest(articleRequestModel);
        await api.articleService.articleServiceErpArticleCreate(countryCode, articleId, erpArticleRequest);
      } else {
        const articleCountryRequest = mapArticleRequestToArticleCountryRequest(articleRequestModel);
        await api.articleService.articleServiceArticleCountryCreate(countryCode, articleId, articleCountryRequest);
      }

      updateValue(0, articleId);
      reload();
      notification.success(t('Speichern erfolgreich'));
    } catch (error) {
      notification.error(t('Fehler beim Speichern, im Zweifel im ERP ändern'));
    }
  }

  const onDeleteArticle = useAsyncCallback(async () => {
    setShowDeleteDialog(false);
    if (articleId > 0) {
      try {
        await api.articleService.articleServiceArticleDelete(countryCode, articleId);
        updateValue(0, 0);
        reload();
        notification.success(t('Löschen erfolgreich'));
      } catch (error) {
        notification.error(t('Fehler beim Löschen, im Zweifel im ERP ändern'));
      }
    }
  });

  const productTypeOptions =
    productTypes?.result?.map((productType) => ({
      key: productType.code,
      value: productType.code,
    })) ?? [];

  // TODO: no data for ContainerTypes available, list below is just for testing
  const containerTypeOptions = [
    { key: 'KE', value: t('Gebindeart_Abkürzung_KE') },
    { key: 'PS', value: t('Gebindeart_Abkürzung_PS') },
    { key: 'KTN', value: t('Gebindeart_Abkürzung_KTN') },
    { key: 'KKA', value: t('Gebindeart_Abkürzung_KKA') },
    { key: 'KFL', value: t('Gebindeart_Abkürzung_KFL') },
    { key: 'BDO', value: t('Gebindeart_Abkürzung_BDO') },
    { key: 'BFL', value: t('Gebindeart_Abkürzung_BFL') },
    { key: 'BHO', value: t('Gebindeart_Abkürzung_BHO') },
    { key: 'BKA', value: t('Gebindeart_Abkürzung_BKA') },
    { key: 'BLE', value: t('Gebindeart_Abkürzung_BLE') },
    { key: 'KDO', value: t('Gebindeart_Abkürzung_KDO') },
    { key: 'KTU', value: t('Gebindeart_Abkürzung_KTU') },
    { key: 'PPE', value: t('Gebindeart_Abkürzung_PPE') },
    { key: 'ROL', value: t('Gebindeart_Abkürzung_ROL') },
    { key: 'EH', value: t('Gebindeart_Abkürzung_EH') },
    { key: 'SET', value: t('Gebindeart_Abkürzung_SET') },
    { key: 'STK', value: t('Gebindeart_Abkürzung_STK') },
    { key: 'BFA', value: t('Gebindeart_Abkürzung_BFA') },
    { key: 'KFA', value: t('Gebindeart_Abkürzung_KFA') },
    { key: 'KHO', value: t('Gebindeart_Abkürzung_KHO') },
    { key: 'CON', value: t('Gebindeart_Abkürzung_CON') },
    { key: 'KS', value: t('Gebindeart_Abkürzung_KS') },
    { key: 'TB', value: t('Gebindeart_Abkürzung_TB') },
    { key: 'PAR', value: t('Gebindeart_Abkürzung_PAR') },
    { key: 'PKG', value: t('Gebindeart_Abkürzung_PKG') },
    { key: 'KG', value: t('Gebindeart_Abkürzung_KG') },
    { key: 'L', value: t('Gebindeart_Abkürzung_L') },
    { key: 'M', value: t('Gebindeart_Abkürzung_M') },
    { key: 'M2', value: t('Gebindeart_Abkürzung_M2') },
    { key: 'M3', value: t('Gebindeart_Abkürzung_M3') },
    { key: 'SAC', value: t('Gebindeart_Abkürzung_SAC') },
    { key: 'BB', value: t('Gebindeart_Abkürzung_BB') },
  ].sort();

  const articleNumberWithoutCountryCode =
    articleResponse.articleNumber !== null && articleResponse.articleNumber !== undefined
      ? articleResponse.articleNumber.substring(0, articleResponse.countryCode?.length ?? 0) ===
        articleResponse.countryCode
        ? articleResponse.articleNumber.substring(
            articleResponse.countryCode?.length ?? 0,
            articleResponse.articleNumber.length
          )
        : articleResponse.articleNumber
      : '';

  const initialValue: ArticleFormData = {
    articleId: articleResponse.id ?? 0,
    materialGroupId: materialGroupId,
    articleDescription: articleResponse.articleDescription ?? '',
    articleNumber: articleNumberWithoutCountryCode,
    ean: articleResponse.ean ?? '',
    containerType: articleResponse.containerType ?? '',
    productTypeCode: articleResponse.productType?.code ?? '',
    palletNumberOfContainers: articleResponse.palletNumberOfContainers ?? null,
    overCartonNumberOfContainers: articleResponse.overCartonNumberOfContainers ?? null,
    articleCountryNumber: articleResponse.articleCountryNumber ?? '',
    deletedArrangementLayoutIds: [],
    addedArrangementLayoutFiles: [],
    ArrangementLayouts: articleResponse.arrangementLayouts ?? [],
    isGlobal: articleResponse.isGlobal ?? true,
    articleNumberComplexB: articleResponse.articleNumberComplexB ?? '',
    priceGroup: articleResponse.priceGroup ?? '',
    v5TextColumn1: articleResponse.v5TextColumn1 ?? '',
    v5TextColumn2: articleResponse.v5TextColumn2 ?? '',
    color: articleResponse.color ?? '',
  };

  const eanCodeMaxLength = 13;
  const articleSchema = Yup.object().shape({
    articleDescription: Yup.string().required(t('Feld erforderlich')),
    articleNumber: Yup.string().matches(RegExp('^\\d+$'), t('Nur Ziffern erlaubt')).required(t('Feld erforderlich')),
    ean: Yup.string()
      .matches(RegExp('^\\d+$'), t('Nur Ziffern erlaubt'))
      .max(eanCodeMaxLength, t('Feld maximal Stellen', { maxFieldLength: eanCodeMaxLength })),
    palletNumberOfContainers: Yup.number()
      .min(1, t('Minimal erlaubter Wert ist', { minValue: 1 }))
      .max(99999, t('Maximal erlaubter Wert ist', { maxValue: 99999 }))
      .nullable(),
    overCartonNumberOfContainers: Yup.number().typeError(t('Nur Ziffern erlaubt')).nullable(),
  });

  if (productTypes.loading) {
    return <CircleLoader open />;
  } else {
    return (
      <>
        <Grid container>
          <Grid item xs={12}>
            <Formik
              initialValues={initialValue}
              onSubmit={updateArticle}
              validationSchema={articleSchema}
              enableReinitialize>
              {({ values, isSubmitting, dirty, isValid, handleReset, handleSubmit, setFieldValue }) => (
                <Form>
                  <Grid container direction='row' alignItems='flex-start' className={classes.buttonContainer}>
                    {readOnly ? (
                      <PIMButton
                        className={classes.topButtons}
                        icon='close'
                        disabled={isSubmitting}
                        onClick={() => {
                          handleReset();
                          updateValue(0, 0);
                          reload();
                        }}>
                        {t('Schließen')}
                      </PIMButton>
                    ) : (
                      <>
                        <PIMButton
                          className={classes.topButtons}
                          icon='close'
                          disabled={isSubmitting}
                          onClick={() => {
                            notification.info(t('Daten zurücksetzen!'));
                            handleReset();
                            updateValue(0, 0);
                            reload();
                            history.push('0');
                          }}>
                          {t('Abbrechen')}
                        </PIMButton>
                        <PIMButton
                          color='primary'
                          icon='save'
                          className={classes.topButtons}
                          busy={isSubmitting}
                          disabled={isSubmitting || !dirty || !isValid}
                          onClick={() => {
                            handleSubmit();
                            history.push('0');
                          }}>
                          {t('Speichern')}
                        </PIMButton>
                      </>
                    )}
                  </Grid>
                  <Grid item xs={12} className={`${classes.titleContainer} ${classes.gridItem}`}>
                    <div className={classes.title}>
                      {readOnly
                        ? t('Artikel Details')
                        : editArticle
                        ? t('Artikel bearbeiten')
                        : t('Neuen Artikel hinzufügen')}
                    </div>
                    {erpImport ? <ERPIcon className={classes.erpIcon} /> : <NonERPIcon className={classes.erpIcon} />}
                  </Grid>
                  {erpImport ? (
                    <PIMInformationBox className={`${classes.titleContainer} ${classes.gridItem}`}>
                      <span>
                        {t('InfoAusERPTeil1')}
                        <br />
                        {t('InfoAusERPTeil2A')}
                      </span>
                    </PIMInformationBox>
                  ) : !articleOwner && editArticle ? (
                    <PIMInformationBox className={`${classes.titleContainer} ${classes.gridItem}`}>
                      <span>
                        {t('InfoAnderesLandTeil1')}
                        <br />
                        {t('InfoAnderesLandTeil2')}
                      </span>
                    </PIMInformationBox>
                  ) : null}
                  <Grid item xs={12}>
                    <FieldArray
                      name='ArrangementLayouts'
                      render={({ push, remove }) => {
                        const nothingToShowToReader = readOnly && values.ArrangementLayouts.length === 0;
                        return nothingToShowToReader ? null : (
                          <div className={classes.documents}>
                            <hr className={classes.documentsLine} />
                            {readOnly ? (
                              <div className={classes.subTitle}>{t('Gebindelayout Dokumente')}</div>
                            ) : (
                              <FileSelectButton
                                onFilesSelected={(files: File[]) => {
                                  push({
                                    articleId: values.articleId,
                                    countryCode,
                                    displayName: files[0].name,
                                    fileName: files[0].name,
                                  } as ArrangementLayoutResponse);
                                  setFieldValue('addedArrangementLayoutFiles', [
                                    ...values.addedArrangementLayoutFiles,
                                    files[0],
                                  ]);
                                }}
                                disabled={values.ArrangementLayouts.length >= MAX_ALLOWED_LAYOUTS}
                                color='secondary'
                                accept={['application/pdf']}
                                warningText={t('Nur PDFs sind erlaubt')}
                                text={t('Anordnungslayout')}
                              />
                            )}

                            {values.ArrangementLayouts?.map((layout: ArrangementLayoutResponse, index: number) => {
                              const isNewLayout = !layout.id;
                              const layoutDownloadLink = !layout.id
                                ? ''
                                : `/api/ArticleService/${countryCode}/arrangementLayout/${layout.id}`;

                              return (
                                <div key={index}>
                                  <PIMFormikTextField
                                    label={t('Gebindelayout Dokument', { documentNumber: index + 1 })}
                                    name={`ArrangementLayouts[${index}].displayName`}
                                    disabled
                                    fullWidth
                                  />
                                  <IconButton
                                    component={Link}
                                    disabled={isNewLayout}
                                    className={`${isNewLayout ? classes.disabled : ''} ${classes.iconOffset}`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    href={layoutDownloadLink}>
                                    <LinkIcon />
                                  </IconButton>
                                  {readOnly ? null : (
                                    <PIMIconButton
                                      className={classes.iconOffset}
                                      disabled={readOnly}
                                      onClick={() => {
                                        if (!isNewLayout) {
                                          setFieldValue('deletedArrangementLayoutIds', [
                                            ...values.deletedArrangementLayoutIds,
                                            layout.id,
                                          ]);
                                        } else {
                                          const removedFile = values.addedArrangementLayoutFiles.find(
                                            (file) => file.name === layout.fileName
                                          );
                                          const updatedFiles = values.addedArrangementLayoutFiles.filter(
                                            (file) => file !== removedFile
                                          );
                                          setFieldValue('addedArrangementLayoutFiles', updatedFiles);
                                        }
                                        remove(index);
                                      }}
                                      iconName='trash'
                                    />
                                  )}
                                </div>
                              );
                            })}
                            <hr className={classes.documentsLine} />
                          </div>
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      label={t('Artikelbezeichnung')}
                      name='articleDescription'
                      required
                      fullWidth
                      disabled={readOnly || (articleOwner && erpImport)}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      label={t('Artikelnummer')}
                      name='articleNumber'
                      required
                      InputProps={{
                        startAdornment: <InputAdornment position='start'>{articleResponse.countryCode}</InputAdornment>,
                      }}
                      fullWidth
                      disabled={readOnly || erpImport || (!articleOwner && editArticle)}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      label={t('EAN Code')}
                      name='ean'
                      fullWidth
                      disabled={readOnly || erpImport || (!articleOwner && editArticle)}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikSelect
                      name='containerType'
                      label={t('Gebindeart')}
                      options={containerTypeOptions}
                      formControlProps={{ fullWidth: true }}
                      disabled={readOnly || erpImport || (!articleOwner && editArticle)}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikSelect
                      name='productTypeCode'
                      label={t('Produktart')}
                      options={productTypeOptions}
                      formControlProps={{ fullWidth: true }}
                      disabled={readOnly || (articleOwner && erpImport)}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      label={t('Anzahl je Palette')}
                      name='palletNumberOfContainers'
                      type='number'
                      fullWidth
                      disabled={readOnly || erpImport || (!articleOwner && editArticle)}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      label={t('Überverpackung')}
                      name='overCartonNumberOfContainers'
                      type='number'
                      fullWidth
                      disabled={readOnly || erpImport || (!articleOwner && editArticle)}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      name='articleCountryNumber'
                      label={t('Zusätzliche Artikelnummer')}
                      fullWidth
                      disabled={readOnly}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      name='articleNumberComplexB'
                      label={t('Verknüpfung Komplex B')}
                      fullWidth
                      disabled={readOnly}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField name='priceGroup' label={t('Preisgruppe')} fullWidth disabled={readOnly} />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      name='v5TextColumn1'
                      label={`${t('V5-Text Spalte')} 1`}
                      fullWidth
                      disabled={readOnly}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField
                      name='v5TextColumn2'
                      label={`${t('V5-Text Spalte')} 2`}
                      fullWidth
                      disabled={readOnly}
                    />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikTextField name='color' label={t('Farbe')} fullWidth disabled={readOnly} />
                  </Grid>
                  <Grid item xs={12} className={classes.gridItem}>
                    <PIMFormikToggle
                      name='isGlobal'
                      label={t('Global verfügbar')}
                      disabled={readOnly || (!articleOwner && editArticle)}
                    />
                  </Grid>
                  {editArticle && !readOnly && !erpImport && articleOwner ? (
                    <>
                      <hr className={classes.line} />
                      <Grid item xs={12} className={`${classes.gridItem} ${classes.deleteButton}`}>
                        <PIMButton
                          color='warning'
                          icon={'trash'}
                          busy={onDeleteArticle.loading}
                          disabled={isSubmitting}
                          onClick={() => setShowDeleteDialog(true)}>
                          {t('Artikel löschen')}
                        </PIMButton>
                      </Grid>
                    </>
                  ) : null}
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
        <MessageDialog
          show={showDeleteDialog}
          messageText={t(`Möchten Sie diesen Artikel löschen?`)}
          title={t('Artikel löschen')}
          confirmColor='warning'
          confirmIcon='trash'
          confirmLabel={t('Löschen')}
          onClose={() => setShowDeleteDialog(false)}
          onCancel={() => setShowDeleteDialog(false)}
          onConfirm={onDeleteArticle.execute}
        />
      </>
    );
  }
}

const useStyles = makeStyles((theme: Theme) => ({
  buttonContainer: {
    marginLeft: '40px',
    marginBottom: '24px',
    marginTop: '72px',
    width: '450px',
  },
  topButtons: { margin: theme.spacing(0, 3, 0, 0) },
  titleContainer: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '20px;',
    font: 'Roboto',
    color: 'black',
    alignItems: 'center',
  },
  subTitle: {
    fontSize: '18px;',
    font: 'Roboto',
    color: 'black',
    alignItems: 'center',
  },
  documents: {
    '& .MuiFormControl-root': {
      width: 'calc(100% - 96px)',
    },
    margin: '24px 40px 0px',
  },
  documentsLine: {
    margin: '16px 0px',
  },
  iconOffset: {
    marginTop: '20px',
  },
  image: {
    marginTop: '20px',
    padding: '0 13px',
  },
  gridItem: {
    display: 'flex',
    marginLeft: '40px',
    marginRight: '40px',
  },
  disabled: { opacity: 0.5 },
  erpIcon: {
    height: '32px',
    marginLeft: '16px',
  },
  line: {
    margin: '16px 40px',
  },
  deleteButton: {
    marginTop: '24px',
    marginBottom: '40px',
  },
}));
